/**
 * Finance Reports math (CRM /crm/finance-reports) — billing amounts, payment merging and
 * the Profit & Loss tab's client / monthly roll-ups. Pure functions only so the Cash Flow
 * and P&L tabs read the same numbers.
 */
import { isActiveClientAccount, resolveClientLifecycle } from './accountLifecycle';
import { normalizeExpenseStatus, parseExpenseDate } from '../services/accountExpensesService';

export function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

export function clientDisplayName(account) {
  const c = account.company?.trim();
  if (c) return c;
  const fn = account.contact?.firstName || '';
  const ln = account.contact?.lastName || '';
  const n = `${fn} ${ln}`.trim();
  return n || 'Client';
}

export function parsePaymentDate(p) {
  const t = p.createdAt?.toDate?.() || p.createdAt || p.date;
  if (!t) return null;
  const d = t instanceof Date ? t : new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Same paid test the client Billing tab uses for Lifetime Amount. */
export function isPaidPayment(p) {
  const s = String(p?.status || '').toLowerCase();
  return s === 'paid' || s === 'succeeded';
}

/**
 * Align Finance Reports with Client Overview: prefer program.monthlyAmount and program.billingCycle
 * when set so CRM values beat a stale or mis-labeled Stripe sync (e.g. weekly price stored as “monthly”).
 */
export function financeBillingSnapshot(account) {
  const b = account.billing || {};
  const p = account.program || {};
  const monthlyAmount =
    Number(p.monthlyAmount) > 0 ? Number(p.monthlyAmount) : Number(b.monthlyAmount) || 0;
  const billingCycle = String(
    p.billingCycle || b.billingCycle || account.billingCycle || 'monthly',
  ).toLowerCase();
  return { ...b, monthlyAmount, billingCycle };
}

/** Per billing period amount in USD; `monthlyAmount` on snapshot is normalized monthly (MRR) for all cycles. */
export function installmentAmount(billing) {
  const m = Number(billing?.monthlyAmount) || 0;
  const cycle = (billing?.billingCycle || 'monthly').toLowerCase();
  if (cycle === 'annual' || cycle === 'year') return m * 12;
  if (cycle === 'quarterly' || cycle === 'quarter') return m * 3;
  if (cycle === 'weekly' || cycle === 'week') return (m * 12) / 52;
  return m;
}

/**
 * Merge subcollection + embedded billing.payments, newest first.
 * Dedupes duplicate Stripe rows (e.g. webhook retries) by invoice id, session id, or Firestore doc id.
 */
export function collectPaymentsForAccount(account, subPayments) {
  const fromSub = [...(subPayments || [])];
  const embedded = Array.isArray(account?.billing?.payments) ? account.billing.payments : [];
  const combined = [...fromSub, ...embedded];
  combined.sort(
    (a, b) => (parsePaymentDate(b)?.getTime() || 0) - (parsePaymentDate(a)?.getTime() || 0),
  );
  const seenInvoice = new Set();
  const seenSession = new Set();
  const seenDocId = new Set();
  const out = [];
  for (const p of combined) {
    const sid = p.stripeInvoiceId;
    if (sid) {
      if (seenInvoice.has(sid)) continue;
      seenInvoice.add(sid);
    } else {
      const sess = p.stripeSessionId;
      if (sess) {
        if (seenSession.has(sess)) continue;
        seenSession.add(sess);
      } else if (p.id) {
        if (seenDocId.has(p.id)) continue;
        seenDocId.add(p.id);
      }
    }
    out.push(p);
  }
  return out;
}

/** When billing.monthlyAmount is 0, use most recent paid (or open) invoice as recurring charge. */
function inferFromPaidInvoices(payments, billing) {
  if (!payments?.length) return { perCycle: 0, mrr: 0 };
  const pick = payments.find((p) => {
    const st = (p.status || '').toLowerCase();
    const amt = Math.abs(Number(p.amount) || 0);
    return st === 'paid' && amt > 0.01;
  }) || payments.find((p) => {
    const st = (p.status || '').toLowerCase();
    const amt = Math.abs(Number(p.amount) || 0);
    return (st === 'open' || st === 'unpaid') && amt > 0.01;
  });
  if (!pick) return { perCycle: 0, mrr: 0 };
  const amt = Math.abs(Number(pick.amount) || 0);
  const cycle = (billing?.billingCycle || 'monthly').toLowerCase();
  let mrr = amt;
  if (cycle === 'annual' || cycle === 'year') mrr = amt / 12;
  else if (cycle === 'quarterly' || cycle === 'quarter') mrr = amt / 3;
  else if (cycle === 'weekly' || cycle === 'week') mrr = (amt * 52) / 12;
  return { perCycle: amt, mrr: Math.round(mrr * 100) / 100 };
}

export function resolveBillingAmounts(account, subPayments) {
  const b = financeBillingSnapshot(account);
  const payments = collectPaymentsForAccount(account, subPayments);
  const fromDeclared = installmentAmount(b);
  const declaredMrr = Number(b.monthlyAmount) || 0;
  if (fromDeclared > 0) {
    const cycle = (b.billingCycle || 'monthly').toLowerCase();
    const mrr =
      declaredMrr > 0
        ? declaredMrr
        : cycle === 'monthly' || cycle === 'month'
          ? fromDeclared
          : inferFromPaidInvoices(payments, b).mrr;
    return { perCycle: fromDeclared, mrr };
  }
  return inferFromPaidInvoices(payments, b);
}

// ---- Profit & Loss ----

/** Stripe states that carry no MRR even while the client record still reads Active. */
const NO_MRR_STRIPE_STATUSES = new Set(['canceled', 'cancelled', 'incomplete']);

const monthIndex = (d) => d.getFullYear() * 12 + d.getMonth();

function sumPaidExpensesInMonth(expenses, monthDate) {
  const idx = monthIndex(monthDate);
  return (expenses || []).reduce((sum, e) => {
    if (normalizeExpenseStatus(e.status) !== 'paid') return sum;
    const d = parseExpenseDate(e);
    return d && monthIndex(d) === idx ? sum + Math.abs(Number(e.amount) || 0) : sum;
  }, 0);
}

function sumPaidExpenses(expenses) {
  return (expenses || []).reduce(
    (sum, e) => (normalizeExpenseStatus(e.status) === 'paid' ? sum + Math.abs(Number(e.amount) || 0) : sum),
    0,
  );
}

function manualMonthlyExpenses(account) {
  const v = account?.billing?.monthlyExpenses;
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return null;
  return Number(v);
}

/**
 * Monthly expense run-rate for one cost bucket. A manual Billing-tab figure wins; otherwise the
 * larger of this month's and last month's logged (paid) expenses, so a month whose partner
 * payouts haven't all landed yet doesn't flatter profit.
 */
function expenseRunRate(expenses, now, manual = null) {
  if (manual !== null) return { amount: manual, source: 'manual' };
  const thisMonth = sumPaidExpensesInMonth(expenses, now);
  const lastMonth = sumPaidExpensesInMonth(expenses, new Date(now.getFullYear(), now.getMonth() - 1, 1));
  if (thisMonth <= 0 && lastMonth <= 0) return { amount: 0, source: 'none' };
  return thisMonth >= lastMonth
    ? { amount: thisMonth, source: 'this-month' }
    : { amount: lastMonth, source: 'last-month' };
}

const marginOf = (revenue, profit) => (revenue > 0 ? profit / revenue : null);

/**
 * One row per client: current MRR, monthly expense run-rate and profit (active clients only),
 * plus lifetime collected / expenses / profit (every client, ended ones included).
 */
export function buildClientPnlRows({ clients, paymentsByAccountId, expensesByAccountId, now = new Date() }) {
  return (clients || []).map((account) => {
    const subPayments = paymentsByAccountId[account.id] || [];
    const expenses = expensesByAccountId[account.id] || [];
    const payments = collectPaymentsForAccount(account, subPayments);
    const lifecycle = resolveClientLifecycle(account, now);
    const active = isActiveClientAccount(account, now);
    const billing = account.billing || {};
    const stripeEnded =
      !!billing.stripeSubscriptionId && NO_MRR_STRIPE_STATUSES.has(String(billing.status || '').toLowerCase());

    const mrr = active && !stripeEnded ? resolveBillingAmounts(account, subPayments).mrr : 0;
    const run = active ? expenseRunRate(expenses, now, manualMonthlyExpenses(account)) : { amount: 0, source: 'none' };
    const monthlyProfit = mrr - run.amount;

    const paidPayments = payments.filter(isPaidPayment);
    const lifetimeCollected = payments.length
      ? paidPayments.reduce((s, p) => s + Math.abs(Number(p.amount) || 0), 0)
      : Number(billing.lifetimePaid ?? billing.lifetimeAmount ?? 0) || 0;
    const lifetimeExpenses = sumPaidExpenses(expenses);

    return {
      accountId: account.id,
      clientName: clientDisplayName(account),
      lifecycleState: lifecycle.state,
      active,
      mrr,
      monthlyExpenses: run.amount,
      expenseSource: run.source,
      monthlyProfit,
      monthlyMargin: marginOf(mrr, monthlyProfit),
      lifetimeCollected,
      lifetimeExpenses,
      lifetimeProfit: lifetimeCollected - lifetimeExpenses,
      lifetimeMargin: marginOf(lifetimeCollected, lifetimeCollected - lifetimeExpenses),
    };
  });
}

/** Company-wide run-rate: active clients' MRR minus their expenses and internal (non-client) costs. */
export function buildRunRateSummary(clientRows, internalExpenses, now = new Date()) {
  const active = clientRows.filter((r) => r.active);
  const mrr = active.reduce((s, r) => s + r.mrr, 0);
  const clientExpenses = active.reduce((s, r) => s + r.monthlyExpenses, 0);
  const internal = expenseRunRate(internalExpenses, now);
  const expenses = clientExpenses + internal.amount;
  const profit = mrr - expenses;
  return {
    mrr,
    clientExpenses,
    internalExpenses: internal.amount,
    internalSource: internal.source,
    expenses,
    profit,
    margin: marginOf(mrr, profit),
    activeClients: active.length,
  };
}

/**
 * Trailing calendar months (oldest first, current month last): cash collected from paid client
 * invoices vs paid expenses dated in that month — client and internal — and the difference.
 */
export function buildMonthlyPnl({
  clients,
  paymentsByAccountId,
  expensesByAccountId,
  internalExpenses,
  months = 12,
  now = new Date(),
}) {
  const nowIdx = monthIndex(now);
  const rows = [];
  for (let i = months - 1; i >= 0; i -= 1) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
    rows.push({
      key: `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}`,
      label: monthStart.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      longLabel: monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      monthStart,
      isCurrent: i === 0,
      collected: 0,
      clientExpenses: 0,
      internalExpenses: 0,
    });
  }
  const firstIdx = nowIdx - (months - 1);
  const bucket = (d) => {
    if (!d) return null;
    const idx = monthIndex(d) - firstIdx;
    return idx >= 0 && idx < months ? rows[idx] : null;
  };

  (clients || []).forEach((account) => {
    collectPaymentsForAccount(account, paymentsByAccountId[account.id] || []).forEach((p) => {
      if (!isPaidPayment(p)) return;
      const row = bucket(parsePaymentDate(p));
      if (row) row.collected += Math.abs(Number(p.amount) || 0);
    });
    (expensesByAccountId[account.id] || []).forEach((e) => {
      if (normalizeExpenseStatus(e.status) !== 'paid') return;
      const row = bucket(parseExpenseDate(e));
      if (row) row.clientExpenses += Math.abs(Number(e.amount) || 0);
    });
  });
  (internalExpenses || []).forEach((e) => {
    if (normalizeExpenseStatus(e.status) !== 'paid') return;
    const row = bucket(parseExpenseDate(e));
    if (row) row.internalExpenses += Math.abs(Number(e.amount) || 0);
  });

  return rows.map((r) => {
    const expenses = r.clientExpenses + r.internalExpenses;
    const profit = r.collected - expenses;
    return { ...r, expenses, profit, margin: marginOf(r.collected, profit) };
  });
}

/**
 * Every expense dated in the given month (all statuses — only paid ones count toward totals),
 * newest first, tagged with its client ('' = internal).
 */
export function buildMonthExpenseLedger({ clients, expensesByAccountId, internalExpenses, monthStart }) {
  const idx = monthIndex(monthStart);
  const inMonth = (e) => {
    const d = parseExpenseDate(e);
    return d && monthIndex(d) === idx;
  };
  const rows = [];
  (clients || []).forEach((account) => {
    (expensesByAccountId[account.id] || []).filter(inMonth).forEach((e) => {
      rows.push({ ...e, accountId: account.id, clientName: clientDisplayName(account) });
    });
  });
  (internalExpenses || []).filter(inMonth).forEach((e) => {
    rows.push({ ...e, accountId: '', clientName: 'Internal' });
  });
  rows.sort((a, b) => (parseExpenseDate(b)?.getTime() || 0) - (parseExpenseDate(a)?.getTime() || 0));
  return rows;
}

/** Paid ledger rows grouped by partner (subcontractor), largest first. */
export function groupExpensesByPartner(ledgerRows) {
  const map = new Map();
  ledgerRows.forEach((e) => {
    if (normalizeExpenseStatus(e.status) !== 'paid') return;
    const name = e.subcontractorName?.trim() || 'Unassigned';
    const key = e.subcontractorId || name.toLowerCase();
    const entry = map.get(key) || { key, name, amount: 0, count: 0, clients: new Set() };
    entry.amount += Math.abs(Number(e.amount) || 0);
    entry.count += 1;
    entry.clients.add(e.clientName);
    map.set(key, entry);
  });
  return [...map.values()]
    .map((g) => ({ ...g, clients: [...g.clients] }))
    .sort((a, b) => b.amount - a.amount);
}
